import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Scissors, ShieldCheck, Phone, Mail, MapPin, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings, getPhoneLines } from "@/lib/settings";
import { useContent } from "@/lib/content";

function HighlightedTitle({ text }) {
  if (!text) return null;
  const words = text.split(" ");
  const tail = words.splice(-2).join(" ");
  return (
    <>
      {words.join(" ")}{" "}
      <span className="text-[#B45309]">{tail}</span>
    </>
  );
}
import ProductCard from "@/components/ProductCard";
import RazrezConfigurator from "@/components/RazrezConfigurator";

const HERO_IMG = "https://images.unsplash.com/photo-1585773818428-b50bebdc2344?auto=format&fit=crop&w=1200&q=80";
const WORKSHOP_IMG = "https://images.unsplash.com/photo-1560846389-956694677531?auto=format&fit=crop&w=1000&q=80";
const BLADE_IMG = "https://images.unsplash.com/photo-1702200047649-ddefe9d4faa9?auto=format&fit=crop&w=800&q=80";
const GRAIN_IMG = "https://images.unsplash.com/photo-1531824475211-72594993ce2a?auto=format&fit=crop&w=800&q=80";

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [akcija, setAkcija] = useState([]);
  const settings = useSettings();
  const content = useContent();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products", { params: { akcija: true } }).then((r) => setAkcija(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-stretch">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="md:col-span-7 flex flex-col justify-center py-6"
            >
              <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#B45309] mb-5">
                Iverali · Razrez · Okovje
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-[#1A1D1A]">
                <HighlightedTitle text={content.hero_naslov} />
              </h1>
              <p className="mt-6 text-base sm:text-lg text-stone-600 leading-relaxed max-w-xl">
                {content.hero_podnaslov}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#razrez"
                  data-testid="hero-configurator-btn"
                  className="h-12 px-7 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold inline-flex items-center gap-2 hover:bg-[#1E2C20] transition-colors"
                >
                  Razreži iveral po meri <Scissors className="w-4 h-4" />
                </a>
                <Link
                  to="/trgovina"
                  data-testid="hero-catalog-btn"
                  className="h-12 px-7 rounded-full border-2 border-[#2D4030] text-[#2D4030] font-semibold inline-flex items-center gap-2 hover:bg-[#2D4030] hover:text-[#FBF9F5] transition-colors"
                >
                  Razišči katalog <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                {[
                  { n: "600+", l: "izdelkov na zalogi" },
                  { n: "±0,1 mm", l: "natančnost reza" },
                  { n: "48 h", l: "dostava po Sloveniji" },
                ].map((s) => (
                  <div key={s.l}>
                    <p className="font-tech font-bold text-xl text-[#1A1D1A]">{s.n}</p>
                    <p className="text-xs text-stone-500 mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="md:col-span-5 grid grid-cols-2 gap-4"
            >
              <div className="col-span-2 rounded-2xl overflow-hidden h-56 lg:h-64 relative group">
                <img src={content.hero_slika_1} alt="Lesni materiali" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D1A]/60 to-transparent" />
                <p className="absolute bottom-4 left-4 text-[#FBF9F5] font-display font-bold">Les, ki diši po delavnici</p>
              </div>
              <div className="rounded-2xl overflow-hidden h-36 lg:h-44 group">
                <img src={content.hero_slika_2} alt="Rezalno orodje" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="rounded-2xl overflow-hidden h-36 lg:h-44 group">
                <img src={content.hero_slika_3} alt="Struktura lesa" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-3">Katalog</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A]">
                Kategorije izdelkov
              </h2>
            </div>
            <Link to="/trgovina" data-testid="categories-all-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#B45309] hover:gap-2.5 transition-all">
              Vsa ponudba <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                {cat.id === "razrez" ? (
                  <a href="#razrez" data-testid={`category-card-${cat.id}`} className="group block bg-white rounded-2xl border border-[#E4E0D7] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 h-full">
                    <CategoryCardInner cat={cat} content={content} />
                  </a>
                ) : (
                  <Link to={`/trgovina?category=${cat.id}`} data-testid={`category-card-${cat.id}`} className="group block bg-white rounded-2xl border border-[#E4E0D7] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 h-full">
                    <CategoryCardInner cat={cat} content={content} />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {akcija.length > 0 && (
        <section className="py-16 lg:py-24 bg-[#F3EFE6]" data-testid="akcija-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-3">Znižano</p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A]">
                  Akcijska ponudba
                </h2>
              </div>
              <Link to="/trgovina?akcija=1" data-testid="akcija-all-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#B45309] hover:gap-2.5 transition-all">
                Vse akcije <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {akcija.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <RazrezConfigurator />

      <section id="o-nas" className="py-20 lg:py-28" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden h-80 lg:h-[420px]">
            <img src={content.onas_slika} alt="Delavnica HobbyCenter" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-3">O nas</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A] leading-snug">
              {content.onas_naslov}
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed">
              {content.onas_besedilo}
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Scissors, t: "Razrez po meri", d: "CNC natančnost ±0,1 mm" },
                { icon: Truck, t: "Hitra dostava", d: "Po vsej Sloveniji v 48 h" },
                { icon: ShieldCheck, t: "Garancija kakovosti", d: "Certificirani materiali" },
              ].map((f) => (
                <div key={f.t} className="bg-white rounded-xl border border-[#E4E0D7] p-4">
                  <f.icon className="w-5 h-5 text-[#B45309] mb-2" />
                  <p className="text-sm font-semibold text-[#1A1D1A]">{f.t}</p>
                  <p className="text-xs text-stone-500 mt-1">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="kontakt" className="py-20 lg:py-24 bg-[#F3EFE6]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-3">Kontakt</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A] mb-10">
            Kako nas najdete
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Phone, t: "Pokličite nas", d: (
                <span className="block space-y-1">
                  {getPhoneLines(settings).map((l, i) => (
                    <span key={i} className="block">{l.oddelek ? `${l.oddelek}: ` : ""}{l.stevilka}</span>
                  ))}
                </span>
              ), testid: "contact-phone" },
              { icon: Mail, t: "Pišite nam", d: settings.email, testid: "contact-email" },
              { icon: MapPin, t: "Prodajni center", d: `${settings.naslov}, ${settings.posta_kraj}`, testid: "contact-address" },
              { icon: Clock, t: "Odpiralni čas", d: settings.odpiralni_cas, testid: "contact-hours" },
            ].map((c) => (
              <div key={c.t} data-testid={c.testid} className="bg-white rounded-2xl border border-[#E4E0D7] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <c.icon className="w-6 h-6 text-[#B45309] mb-3" />
                <p className="font-display font-bold text-[#1A1D1A]">{c.t}</p>
                <p className="text-sm text-stone-600 mt-1">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function CategoryCardInner({ cat, content }) {
  const key = `kat_${cat.id.replace(/-/g, "_")}`;
  const image = content?.[`${key}_slika`] || cat.image;
  const desc = content?.[`${key}_opis`] || cat.desc;
  return (
    <>
      <div className="relative h-36 overflow-hidden">
        <img src={image} alt={cat.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-[#1A1D1A]/85 text-[#FBF9F5] text-[11px] font-tech px-2.5 py-1 rounded-full backdrop-blur">
          {cat.badge}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-[#1A1D1A] leading-snug">{cat.name}</h3>
        <p className="text-xs text-stone-500 mt-1.5 leading-relaxed line-clamp-2">{desc}</p>
        {cat.id !== "razrez" && (
          <p className="mt-2 text-[11px] font-tech text-stone-400">{cat.count} izdelkov</p>
        )}
      </div>
    </>
  );
}
